import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

const ThemeContext = createContext(undefined)

const STORAGE_KEY = 'eduvera:theme'

const getInitialTheme = () => {
  if (typeof window === 'undefined') {
    return 'light'
  }

  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (stored === 'light' || stored === 'dark') {
    return stored
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => getInitialTheme())
  const hasExplicitPreference = useRef(
    typeof window !== 'undefined' && window.localStorage.getItem(STORAGE_KEY) !== null,
  )

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }

    const root = document.documentElement
    root.classList.toggle('dark', theme === 'dark')
    root.dataset.theme = theme
    root.style.colorScheme = theme === 'dark' ? 'dark' : 'light'

    if (typeof window !== 'undefined') {
      if (hasExplicitPreference.current) {
        window.localStorage.setItem(STORAGE_KEY, theme)
      } else {
        window.localStorage.removeItem(STORAGE_KEY)
      }
    }
  }, [theme])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (event) => {
      if (!hasExplicitPreference.current) {
        setTheme(event.matches ? 'dark' : 'light')
      }
    }

    media.addEventListener('change', handler)
    return () => media.removeEventListener('change', handler)
  }, [])

  const setExplicitTheme = useCallback(
    (value) => {
      hasExplicitPreference.current = true
      setTheme(value)
    },
    [setTheme],
  )

  const toggleTheme = useCallback(() => {
    setExplicitTheme(theme === 'dark' ? 'light' : 'dark')
  }, [setExplicitTheme, theme])

  const value = useMemo(
    () => ({
      theme,
      isDark: theme === 'dark',
      setTheme: setExplicitTheme,
      toggleTheme,
    }),
    [setExplicitTheme, theme, toggleTheme],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
